import {Alert, Box, Chip, CircularProgress, Stack, Typography} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import {useSnackbar} from 'notistack';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import LFCTitledCard from '_components/surfaces/LFCTitledCard';
import {useAuthUser} from '_contexts/AuthUserProvider';
import {useMySettings} from '_contexts/MySettingsProvider';
import {getLFCData, setLFCData} from '_logics/LFCUtil';
import GenericTemplate from '_templates/GenericTemplate';

type Announcement = {
  announcement_id: number;
  start_date: string;
  category: string;
  title: string;
  body: string;
  is_unread?: boolean;
};

const HomePage = () => {
  const {t, i18n} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [information, setInformation] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const authUser = useAuthUser();
  const mySettings = useMySettings();
  const source = useMemo(() => axios.CancelToken.source(), []);

  useEffect(() => {
    return () => {
      // アンマウント処理
      source.cancel('リクエストをキャンセルしてページ移動');
    };
  }, []);

  useEffect(() => {
    if (!authUser) {
      return;
    }

    const locale = mySettings.locale || i18n.language || 'ja';

    const loadAnnouncements = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const data = await getLFCData({
          snack: enqueueSnackbar,
          sql_id: 20001,
          parameters: {locale},
          cancelToken: source.token,
          t
        });
        setInformation(data);

        setLFCData({
          snack: enqueueSnackbar,
          sql_id: 20005,
          parameters: {
            information_access_timestamp: dayjs().format()
          },
          cancelToken: source.token,
          t
        }).catch(error => {
          if (!axios.isCancel(error)) {
            console.warn('Failed to update announcement access timestamp:', error);
          }
        });
      } catch (error) {
        if (!axios.isCancel(error)) {
          setHasError(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadAnnouncements();
  }, [authUser?.companyId, authUser?.userId, i18n.language, mySettings.locale, source]);

  return (
    <GenericTemplate title={t('ホーム')}>
      <Box mt={1}>
        <LFCTitledCard title={t('お知らせ')} transparent>
          {isLoading && (
            <Stack alignItems="center" py={4} spacing={1}>
              <CircularProgress size={28} />
              <Typography color="textSecondary" variant="body2">
                {t('読み込み中')}
              </Typography>
            </Stack>
          )}

          {!isLoading && hasError && (
            <Alert severity="error">{t('message.エラーが発生しました。')}</Alert>
          )}

          {!isLoading && !hasError && information.length === 0 && (
            <Box py={4} textAlign="center">
              <Typography color="textSecondary">{t('お知らせはありません')}</Typography>
            </Box>
          )}

          {!isLoading && !hasError && 0 < information.length && (
            <Stack spacing={2}>
              {information.map(item => (
                <Box
                  key={item.announcement_id}
                  sx={{
                    borderBottom: theme => `1px solid ${theme.palette.divider}`,
                    pb: 2
                  }}
                >
                  <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1} mb={0.5}>
                    <Typography color="textSecondary" variant="caption">
                      {item.start_date}
                    </Typography>
                    <Chip label={t(item.category || '更新')} size="small" variant="outlined" />
                    {item.is_unread && <Chip color="primary" label={t('未読')} size="small" />}
                  </Stack>
                  <Typography fontWeight={600} variant="body1">
                    {item.title}
                  </Typography>
                  <Typography color="textSecondary" sx={{whiteSpace: 'pre-line'}} variant="body2">
                    {item.body}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </LFCTitledCard>
      </Box>
    </GenericTemplate>
  );
};

export default HomePage;
